import type { CSSProperties } from "react";
import { clsx } from "clsx";
import { ShapeRenderer } from "./ShapeRenderer";

const knownShapes = new Set(["circle", "square", "triangle", "diamond", "hexagon", "star", "plus", "arrow", "line", "dot"]);

function getArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function VisualToken({ value, target = false }: { value: string; target?: boolean }) {
  if (knownShapes.has(value)) return <ShapeRenderer spec={{ shape: value, color: target ? "cyan" : "blue", size: "sm" }} />;
  return <span className={target ? "text-lg font-bold text-cyan-100" : "text-lg font-bold text-slate-200"}>{value}</span>;
}

export function PositionGridRenderer({ visualSpec }: { visualSpec: Record<string, unknown> }) {
  const sequence = getArray(visualSpec.sequence);
  const grids = sequence.length ? sequence : [0, 2, 4, null];
  const size = Number(visualSpec.gridSize ?? 3);
  return (
    <div className="grid w-full max-w-2xl grid-cols-3 gap-2 rounded-lg border border-white/10 bg-slate-950/35 p-3 sm:grid-cols-6">
      {grids.map((active, gridIndex) => (
        <div
          key={gridIndex}
          className="grid aspect-square overflow-hidden rounded-md border border-cyan-100/70 bg-cyan-100/70 p-[2px]"
          style={{ gridTemplateColumns: `repeat(${size}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${size}, minmax(0, 1fr))`, gap: 2 }}
        >
          {Array.from({ length: size * size }, (_, cell) => (
            <div key={cell} className="flex min-h-0 min-w-0 items-center justify-center bg-slate-950/90">
              {active === null && cell === Math.floor((size * size) / 2) ? <span className="text-sm font-bold text-cyan-100">?</span> : active === cell ? <span className="block h-3.5 w-3.5 rounded-sm bg-cyan-200 shadow-[0_0_12px_rgb(103_232_249_/_0.55)]" /> : null}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function VisualEquationRenderer({ visualSpec }: { visualSpec: Record<string, unknown> }) {
  const equations = getArray(visualSpec.equations).map(String);
  return (
    <div className="mx-auto grid w-full max-w-xl gap-3 rounded-lg border border-white/10 bg-slate-950/35 p-4">
      {equations.map((equation) => (
        <div key={equation} className="rounded-lg border border-white/10 bg-white/[0.045] px-3 py-3 text-center text-xl font-bold leading-relaxed text-cyan-100 sm:text-2xl">
          {equation}
        </div>
      ))}
    </div>
  );
}

export function DotCornerRenderer({ visualSpec }: { visualSpec: Record<string, unknown> }) {
  const tiles = getArray(visualSpec.tiles);
  const renderTile = (dots: unknown) => {
    const positions = Array.isArray(dots) ? dots.map(String) : [];
    if (dots === null) return <span className="absolute inset-0 flex items-center justify-center text-xl font-bold text-cyan-100">?</span>;
    return positions.map((position, dotIndex) => (
      <span
        key={`${position}-${dotIndex}`}
        className="absolute h-2.5 w-2.5 rounded-full bg-cyan-200"
        style={{
          top: position.includes("top") ? 8 : position === "center" ? "calc(50% - 5px)" : position.includes("mid") ? "calc(50% - 5px)" : undefined,
          bottom: position.includes("bottom") ? 8 : undefined,
          left: position.includes("left") ? 8 : position === "center" ? "calc(50% - 5px)" : undefined,
          right: position.includes("right") ? 8 : undefined
        }}
      />
    ));
  };
  return (
    <div className="grid w-full max-w-md grid-cols-5 gap-2 rounded-lg border border-white/10 bg-slate-950/35 p-3">
      {tiles.map((dots, index) => (
        <div key={index} className="relative aspect-square rounded-lg border border-white/10 bg-white/[0.045]">
          {renderTile(dots)}
        </div>
      ))}
    </div>
  );
}

function dotBlockPositions(value: unknown) {
  if (value === null) return null;
  const numericPositions: Record<string, string> = {
    "1": "top-left",
    "2": "top",
    "3": "top-right",
    "4": "left",
    "5": "center",
    "6": "right",
    "7": "bottom-left",
    "8": "bottom",
    "9": "bottom-right"
  };
  if (Array.isArray(value)) return value.map((position) => numericPositions[String(position)] ?? String(position));
  const side = String(value);
  const sideMap: Record<string, string[]> = {
    left: ["top-left", "bottom-left"],
    top: ["top-left", "top-right"],
    right: ["top-right", "bottom-right"],
    bottom: ["bottom-left", "bottom-right"]
  };
  return sideMap[side] ?? [];
}

function DotGridGuides() {
  return (
    <span className="pointer-events-none absolute inset-0 z-0 rounded-lg">
      <span className="absolute bottom-0 left-1/3 top-0 w-[2px] bg-cyan-200/40" />
      <span className="absolute bottom-0 left-2/3 top-0 w-[2px] bg-cyan-200/40" />
      <span className="absolute left-0 right-0 top-1/3 h-[2px] bg-cyan-200/40" />
      <span className="absolute left-0 right-0 top-2/3 h-[2px] bg-cyan-200/40" />
    </span>
  );
}

function DotSequenceBlock({ dots, showGrid = false }: { dots: string[] | null; showGrid?: boolean }) {
  const positionStyle = (position: string) => {
    const center = "calc(50% - 7px)";
    const near = "calc(16.6667% - 7px)";
    const far = "calc(83.3333% - 7px)";
    const middleA = "calc(33.3333% - 7px)";
    const middleB = "calc(66.6667% - 7px)";
    const map: Record<string, CSSProperties> = {
      center: { top: center, left: center },
      "top-left": { top: near, left: near },
      top: { top: near, left: center },
      "top-right": { top: near, left: far },
      left: { top: center, left: near },
      right: { top: center, left: far },
      "bottom-left": { top: far, left: near },
      bottom: { top: far, left: center },
      "bottom-right": { top: far, left: far },
      "left-upper": { top: near, left: near },
      "left-lower": { top: far, left: near },
      "right-upper": { top: near, left: far },
      "right-lower": { top: far, left: far }
    };
    return map[position] ?? map.center;
  };

  return (
    <div className={clsx("relative aspect-square min-w-0 overflow-hidden rounded-lg bg-slate-950/70 shadow-inner shadow-cyan-950/20", showGrid ? "border border-cyan-100/55" : "border border-cyan-200/35")}>
      {showGrid ? <DotGridGuides /> : null}
      {dots === null ? <span className="absolute inset-0 z-10 flex items-center justify-center text-2xl font-bold text-cyan-100">?</span> : null}
      {dots?.map((position, index) => (
        <span
          key={`${position}-${index}`}
          className="absolute z-10 h-3.5 w-3.5 rounded-full bg-cyan-200 shadow-[0_0_14px_rgb(103_232_249_/_0.65)]"
          style={positionStyle(position)}
        />
      ))}
    </div>
  );
}

export function DotMovementSequenceRenderer({ visualSpec }: { visualSpec: Record<string, unknown> }) {
  const sequence = getArray(visualSpec.sequence);
  const blocks = sequence.length ? sequence : ["left", "top", "right", "bottom", "left", null];
  const showGrid = visualSpec.showGrid === true;

  return (
    <div className="w-full max-w-3xl rounded-lg border border-white/10 bg-slate-950/35 p-3 sm:p-4">
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-8">
        {blocks.map((block, index) => (
          <DotSequenceBlock key={`${String(block)}-${index}`} dots={dotBlockPositions(block)} showGrid={showGrid} />
        ))}
      </div>
    </div>
  );
}

type ColorBlockCell = "green" | "red" | "blue" | "yellow" | "gray";

function colorClass(color: string) {
  const colors: Record<string, string> = {
    green: "bg-emerald-400",
    red: "bg-rose-500",
    blue: "bg-sky-400",
    yellow: "bg-amber-300",
    gray: "bg-slate-700/65"
  };
  return colors[color] ?? colors.gray;
}

function ColorSequenceBlock({
  block,
  selected = false,
  correct = false,
  readOnly = false,
  onSelect
}: {
  block: Record<string, unknown>;
  selected?: boolean;
  correct?: boolean;
  readOnly?: boolean;
  onSelect?: (label: string) => void;
}) {
  const label = String(block.label ?? "");
  const cells = (block.cells && typeof block.cells === "object" ? block.cells : {}) as Partial<Record<"tl" | "tr" | "bl" | "br", ColorBlockCell>>;
  const cellOrder: Array<"tl" | "tr" | "bl" | "br"> = ["tl", "tr", "bl", "br"];

  return (
    <button
      type="button"
      disabled={readOnly}
      onClick={() => {
        if (!readOnly) onSelect?.(label);
      }}
      className={clsx(
        "grid gap-1 rounded-lg border p-1 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-200",
        correct
          ? "border-emerald-200/85 bg-emerald-200/10 shadow-[0_0_22px_rgb(110_231_183_/_0.15)]"
          : selected
            ? "border-cyan-200/85 bg-cyan-200/10 shadow-[0_0_22px_rgb(34_211_238_/_0.16)]"
            : "border-transparent hover:border-cyan-200/45 hover:bg-white/[0.045]",
        readOnly ? "cursor-default hover:border-transparent hover:bg-transparent" : ""
      )}
      aria-pressed={selected || correct}
    >
      <span className="text-center text-xs font-bold text-slate-300">{label}</span>
      <div className="grid aspect-square grid-cols-2 gap-1 rounded-lg border border-white/10 bg-slate-950/70 p-1 shadow-inner shadow-cyan-950/20">
        {cellOrder.map((cell) => (
          <span key={cell} className={`rounded-[5px] border border-white/10 ${colorClass(String(cells[cell] ?? "gray"))}`} />
        ))}
      </div>
    </button>
  );
}

export function ColorBlockSequenceRenderer({
  visualSpec,
  selectedOption,
  onSelect,
  readOnly = false,
  revealCorrectOption
}: {
  visualSpec: Record<string, unknown>;
  selectedOption?: string | null;
  onSelect?: (optionId: string) => void;
  readOnly?: boolean;
  revealCorrectOption?: string;
}) {
  const blocks = getArray(visualSpec.blocks).filter((block): block is Record<string, unknown> => Boolean(block && typeof block === "object"));

  return (
    <div className="w-full max-w-4xl rounded-lg border border-white/10 bg-slate-950/35 p-3 sm:p-4">
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-9">
        {blocks.map((block, index) => (
          <ColorSequenceBlock
            key={`${String(block.label ?? index)}-${index}`}
            block={block}
            selected={selectedOption === block.label}
            correct={revealCorrectOption === block.label}
            readOnly={readOnly}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

function circleSegmentColor(color: string) {
  const colors: Record<string, string> = {
    blue: "rgb(56 189 248)",
    green: "rgb(52 211 153)",
    orange: "rgb(251 146 60)"
  };
  return colors[color] ?? colors.blue;
}

function CircleHalfFillCell({ cell, id }: { cell: Record<string, unknown> | null; id: string }) {
  if (!cell) {
    return <div className="flex aspect-square items-center justify-center rounded-lg border border-dashed border-cyan-200/45 bg-slate-950/70 text-3xl font-bold text-cyan-100">?</div>;
  }

  const color = circleSegmentColor(String(cell.color ?? "blue"));
  const line = String(cell.line ?? "vertical");
  const clipId = `circle-half-${id}`;
  const fillShape =
    line === "horizontal" ? (
      <path d="M18 18H82V50H18Z" fill={color} clipPath={`url(#${clipId})`} />
    ) : line === "diagonal" ? (
      <path d="M18 18H82L18 82Z" fill={color} clipPath={`url(#${clipId})`} />
    ) : (
      <path d="M18 18H50V82H18Z" fill={color} clipPath={`url(#${clipId})`} />
    );
  const separator =
    line === "horizontal" ? <line x1="20" y1="50" x2="80" y2="50" /> : line === "diagonal" ? <line x1="0" y1="100" x2="100" y2="0" clipPath={`url(#${clipId})`} /> : <line x1="50" y1="20" x2="50" y2="80" />;

  return (
    <div className="flex aspect-square items-center justify-center rounded-lg border border-white/10 bg-slate-950/70 p-2">
      <svg className="h-full w-full" viewBox="0 0 100 100" role="img" aria-label="Κύκλος με χρώμα και γραμμή">
        <defs>
          <clipPath id={clipId}>
            <circle cx="50" cy="50" r="32" />
          </clipPath>
        </defs>
        <circle cx="50" cy="50" r="32" fill="rgb(15 23 42)" stroke="rgb(148 163 184)" strokeWidth="3" />
        {fillShape}
        <g stroke="rgb(226 232 240)" strokeWidth="4" strokeLinecap="round">
          {separator}
        </g>
        <circle cx="50" cy="50" r="32" fill="none" stroke="rgb(203 213 225)" strokeWidth="3" />
      </svg>
    </div>
  );
}

export function CircleHalfFillMatrixRenderer({ visualSpec }: { visualSpec: Record<string, unknown> }) {
  const cells = getArray(visualSpec.cells);
  return (
    <div className="w-full max-w-lg rounded-lg border border-white/10 bg-slate-950/35 p-3 sm:p-4">
      <div className="grid grid-cols-3 gap-2">
        {cells.map((cell, index) => (
          <CircleHalfFillCell key={index} id={`main-${index}`} cell={cell && typeof cell === "object" ? (cell as Record<string, unknown>) : null} />
        ))}
      </div>
    </div>
  );
}

function segmentSet(value: unknown) {
  return new Set(Array.isArray(value) ? value.map(String) : []);
}

function CircleSegmentUnionShape({ segments, centerBlack = false }: { segments: Set<string>; centerBlack?: boolean }) {
  const segmentPaths = {
    top: "M-4 -4H104L65 42H35Z",
    right: "M104 -4V104L58 65V35Z",
    bottom: "M104 104H-4L35 58H65Z",
    left: "M-4 104V-4L42 35V65Z"
  };
  const dividerLines = [
    { x1: 20.302, y1: 20.302, x2: 38, y2: 38 },
    { x1: 79.698, y1: 20.302, x2: 62, y2: 38 },
    { x1: 79.698, y1: 79.698, x2: 62, y2: 62 },
    { x1: 20.302, y1: 79.698, x2: 38, y2: 62 }
  ];

  return (
    <svg className="h-full w-full max-w-[118px]" viewBox="0 0 100 100" role="img" aria-label="Κύκλος με τμήματα" shapeRendering="geometricPrecision">
      <defs>
        <clipPath id="circle-segment-union-clip">
          <circle cx="50" cy="50" r="42" />
        </clipPath>
      </defs>
      <circle cx="50" cy="50" r="42" fill="rgb(255 255 255)" />
      <g clipPath="url(#circle-segment-union-clip)">
        {Object.entries(segmentPaths).map(([id, d]) => (
          <path key={id} d={d} fill={segments.has(id) ? "rgb(0 0 0)" : "rgb(255 255 255)"} />
        ))}
      </g>
      <g stroke="rgb(0 0 0)" strokeWidth="2.4" strokeLinecap="butt">
        {dividerLines.map((line, index) => (
          <line key={index} {...line} />
        ))}
      </g>
      <rect x="38" y="38" width="24" height="24" fill={centerBlack ? "rgb(0 0 0)" : "rgb(255 255 255)"} stroke="rgb(0 0 0)" strokeWidth="2.8" />
      <circle cx="50" cy="50" r="42" fill="none" stroke="rgb(0 0 0)" strokeWidth="3" />
    </svg>
  );
}

function CircleSegmentUnionCell({ cell }: { cell: Record<string, unknown> | null }) {
  if (!cell) {
    return <div className="flex aspect-square items-center justify-center rounded-md border border-dashed border-slate-400 bg-slate-100 text-4xl font-bold text-slate-500">?</div>;
  }
  return <CircleSegmentUnionShape segments={segmentSet(cell.segments)} centerBlack={cell.centerBlack === true} />;
}

export function CircleSegmentUnionMatrixRenderer({ visualSpec }: { visualSpec: Record<string, unknown> }) {
  const cells = getArray(visualSpec.cells);
  return (
    <div className="w-full max-w-lg rounded-lg border border-white/10 bg-slate-950/35 p-3 sm:p-4">
      <div className="grid grid-cols-3 gap-2 rounded-md border border-slate-300 bg-slate-100 p-2">
        {cells.map((cell, index) => (
          <div key={index} className="flex aspect-square items-center justify-center rounded-sm border border-slate-300 bg-white p-2">
            <CircleSegmentUnionCell cell={cell && typeof cell === "object" ? (cell as Record<string, unknown>) : null} />
          </div>
        ))}
      </div>
    </div>
  );
}

const pieSlices = ["top", "top-right", "right", "bottom-right", "bottom", "bottom-left", "left", "top-left"];

function polarPoint(cx: number, cy: number, radius: number, angle: number) {
  const radians = ((angle - 90) * Math.PI) / 180;
  return { x: cx + radius * Math.cos(radians), y: cy + radius * Math.sin(radians) };
}

function pieSlicePath(index: number) {
  const start = polarPoint(50, 50, 38, index * 45 - 22.5);
  const end = polarPoint(50, 50, 38, index * 45 + 22.5);
  return `M50 50L${start.x.toFixed(3)} ${start.y.toFixed(3)}A38 38 0 0 1 ${end.x.toFixed(3)} ${end.y.toFixed(3)}Z`;
}

function PieSliceShape({ slices }: { slices: Set<string> }) {
  return (
    <svg className="h-full w-full max-w-[118px]" viewBox="0 0 100 100" role="img" aria-label="Κύκλος με οκτώ τμήματα" shapeRendering="geometricPrecision">
      <circle cx="50" cy="50" r="38" fill="rgb(255 255 255)" />
      {pieSlices.map((slice, index) => (
        <path key={slice} d={pieSlicePath(index)} fill={slices.has(slice) ? "rgb(45 212 191)" : "rgb(255 255 255)"} />
      ))}
      {Array.from({ length: 8 }, (_, index) => {
        const point = polarPoint(50, 50, 38, index * 45 - 22.5);
        return <line key={index} x1="50" y1="50" x2={point.x} y2={point.y} stroke="rgb(0 0 0)" strokeWidth="1.6" />;
      })}
      <circle cx="50" cy="50" r="38" fill="none" stroke="rgb(0 0 0)" strokeWidth="2.6" />
    </svg>
  );
}

function PieSliceCell({ cell }: { cell: Record<string, unknown> | null }) {
  if (!cell) {
    return <div className="flex aspect-square items-center justify-center rounded-md border border-dashed border-slate-400 bg-slate-100 text-4xl font-bold text-slate-500">?</div>;
  }
  return <PieSliceShape slices={segmentSet(cell.slices)} />;
}

export function PieSliceUnionMatrixRenderer({ visualSpec }: { visualSpec: Record<string, unknown> }) {
  const cells = getArray(visualSpec.cells);
  return (
    <div className="w-full max-w-lg rounded-lg border border-white/10 bg-slate-950/35 p-3 sm:p-4">
      <div className="grid grid-cols-3 gap-2 rounded-md border border-slate-300 bg-slate-100 p-2">
        {cells.map((cell, index) => (
          <div key={index} className="flex aspect-square items-center justify-center rounded-sm border border-slate-300 bg-white p-2">
            <PieSliceCell cell={cell && typeof cell === "object" ? (cell as Record<string, unknown>) : null} />
          </div>
        ))}
      </div>
    </div>
  );
}

const isoTopViewColors = {
  red: "#ef1b22",
  green: "#0b9446",
  yellow: "#f3e71a",
  orange: "#ee7c1c",
  blue: "#20a7dd",
  stroke: "#2f2f2f"
};

function IsometricTopViewShape() {
  const strokeProps = {
    stroke: isoTopViewColors.stroke,
    strokeWidth: 1.8,
    strokeLinejoin: "round" as const,
    strokeLinecap: "round" as const,
    vectorEffect: "non-scaling-stroke" as const
  };

  return (
    <svg className="h-auto w-full max-w-md" viewBox="0 0 133 126" role="img" aria-label="Ισομετρικό χρωματιστό σχήμα" shapeRendering="geometricPrecision">
      <polygon points="2,50 23,38 23,62" fill={isoTopViewColors.yellow} {...strokeProps} />
      <polygon points="128,50 107,38 107,62" fill={isoTopViewColors.green} {...strokeProps} />
      <polygon
        points="23,38 44,50 44,74 65,86 65,110 44,98 44,122 23,110 23,86 2,74 2,50 23,62"
        fill={isoTopViewColors.green}
        {...strokeProps}
      />
      <polygon
        points="107,38 86,50 86,74 65,86 65,110 86,98 86,122 107,110 107,86 128,74 128,50 107,62"
        fill={isoTopViewColors.yellow}
        {...strokeProps}
      />
      <polygon
        points="44,2 65,14 86,2 107,14 86,26 107,38 86,50 65,38 44,50 23,38 44,26 23,14"
        fill={isoTopViewColors.red}
        {...strokeProps}
      />
      <polygon points="23,14 44,26 23,38" fill={isoTopViewColors.yellow} {...strokeProps} />
      <polygon points="107,14 86,26 107,38" fill={isoTopViewColors.green} {...strokeProps} />
      <polygon points="65,38 86,50 86,74 65,86 44,74 44,50" fill={isoTopViewColors.orange} {...strokeProps} />
      <line x1="65" y1="38" x2="65" y2="62" {...strokeProps} fill="none" />
      <line x1="44" y1="74" x2="65" y2="62" {...strokeProps} fill="none" />
      <line x1="86" y1="74" x2="65" y2="62" {...strokeProps} fill="none" />
      <polygon points="44,98 65,110 44,122" fill={isoTopViewColors.blue} {...strokeProps} />
      <polygon points="86,98 65,110 86,122" fill={isoTopViewColors.blue} {...strokeProps} />
    </svg>
  );
}

export function TopViewIdentificationRenderer() {
  return (
    <div className="flex w-full max-w-2xl justify-center rounded-lg border border-white/10 bg-slate-950/35 p-4 sm:p-5">
      <div className="flex w-full justify-center rounded-md border border-slate-300 bg-slate-100 p-4 sm:p-6">
        <IsometricTopViewShape />
      </div>
    </div>
  );
}

const symbolGridSymbols = ["T", "C", "D", "P"];

function SymbolGridIcon({ symbol }: { symbol: string }) {
  if (symbol === "C") return <span className="block h-4 w-4 rounded-full bg-black sm:h-5 sm:w-5" />;
  if (symbol === "D") return <span className="block h-4 w-4 rotate-45 bg-black sm:h-5 sm:w-5" />;
  if (symbol === "P") {
    return (
      <svg className="h-6 w-6 sm:h-7 sm:w-7" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 2 22 9.2 18.2 21H5.8L2 9.2Z" fill="rgb(0 0 0)" />
      </svg>
    );
  }
  return (
    <svg className="h-6 w-6 sm:h-7 sm:w-7" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3 22 21H2Z" fill="rgb(0 0 0)" />
    </svg>
  );
}

function readSymbolGrid(value: unknown) {
  return Array.isArray(value) ? value.map((row) => (Array.isArray(row) ? row.map((symbol) => (symbolGridSymbols.includes(String(symbol)) ? String(symbol) : "T")) : [])) : [];
}

function SymbolGridCard({ grid, missing = false }: { grid: string[][]; missing?: boolean }) {
  if (missing) {
    return <div className="flex aspect-square w-full items-center justify-center rounded-md border-2 border-dashed border-slate-400 bg-slate-100 text-4xl font-bold text-slate-500 shadow-inner">?</div>;
  }

  return (
    <div className="grid aspect-square w-full grid-cols-3 overflow-hidden rounded-md border-2 border-slate-900 bg-slate-300 p-px shadow-sm">
      {grid.flat().map((symbol, index) => (
        <span key={`${symbol}-${index}`} className="flex min-h-0 min-w-0 items-center justify-center border border-slate-300 bg-slate-50">
          <SymbolGridIcon symbol={symbol} />
        </span>
      ))}
    </div>
  );
}

function SymbolGridSequenceCell({ cell }: { cell: unknown }) {
  if (cell === null) return <SymbolGridCard grid={[]} missing />;
  const grid = readSymbolGrid(cell);
  return <SymbolGridCard grid={grid.length ? grid : [["T", "C", "D"], ["P", "T", "C"], ["D", "P", "T"]]} />;
}

export function SymbolGridStepSequenceRenderer({ visualSpec }: { visualSpec: Record<string, unknown> }) {
  const cards = getArray(visualSpec.cards);
  return (
    <div className="w-full max-w-3xl rounded-lg border border-white/10 bg-slate-950/35 p-3 sm:p-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
        {cards.map((cell, index) => (
          <div key={index} className="rounded-lg border border-slate-300 bg-white p-2 shadow-sm sm:p-3">
            <SymbolGridSequenceCell cell={cell} />
          </div>
        ))}
      </div>
    </div>
  );
}

type CircleDotState = "dark" | "gold" | "empty";

function BlueCircleDotCard({ dots, missing = false }: { dots: Partial<Record<"top" | "right" | "bottom" | "left" | "center", CircleDotState>>; missing?: boolean }) {
  if (missing) {
    return <div className="flex aspect-square items-center justify-center rounded-md border border-dashed border-slate-400 bg-slate-100 text-4xl font-bold text-slate-500">?</div>;
  }

  const positions = [
    { id: "top", cx: 50, cy: 24 },
    { id: "right", cx: 76, cy: 50 },
    { id: "bottom", cx: 50, cy: 76 },
    { id: "left", cx: 24, cy: 50 },
    { id: "center", cx: 50, cy: 50 }
  ] as const;
  const fill = (state: CircleDotState | undefined) => (state === "gold" ? "rgb(245 196 45)" : "rgb(15 23 42)");

  return (
    <svg className="h-full w-full max-w-[118px]" viewBox="0 0 100 100" role="img" aria-label="Μπλε κύκλος με κουκίδες">
      <circle cx="50" cy="50" r="40" fill="rgb(59 130 246)" stroke="rgb(15 23 42)" strokeWidth="2.6" />
      {positions.map((position) => {
        const state = dots[position.id];
        if (!state || state === "empty") return null;
        return <circle key={position.id} cx={position.cx} cy={position.cy} r="7" fill={fill(state)} stroke="rgb(15 23 42)" strokeWidth="1.2" />;
      })}
    </svg>
  );
}

function readCircleDotCell(cell: unknown) {
  return cell && typeof cell === "object" ? (cell as Partial<Record<"top" | "right" | "bottom" | "left" | "center", CircleDotState>>) : {};
}

export function BlueCircleDotComparisonMatrixRenderer({ visualSpec }: { visualSpec: Record<string, unknown> }) {
  const cells = getArray(visualSpec.cells);
  return (
    <div className="w-full max-w-2xl rounded-lg border border-white/10 bg-slate-950/35 p-3 sm:p-4">
      <div className="grid grid-cols-3 gap-2 rounded-md border border-slate-300 bg-slate-100 p-2">
        {cells.map((cell, index) => (
          <div key={index} className="flex aspect-square items-center justify-center rounded-sm border border-slate-300 bg-white p-1.5 sm:p-2">
            {cell === null ? <BlueCircleDotCard dots={{}} missing /> : <BlueCircleDotCard dots={readCircleDotCell(cell)} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function neonGridColor(color: string) {
  const colors: Record<string, string> = {
    P: "#9b4dff",
    B: "#2f86ff",
    K: "#05070b",
    L: "#a2ef28"
  };
  return colors[color] ?? colors.P;
}

function readColorGrid(value: unknown) {
  return Array.isArray(value) ? value.map((row) => (Array.isArray(row) ? row.map(String) : [])) : [];
}

function NeonColorGridTile({ grid, missing = false }: { grid: string[][]; missing?: boolean }) {
  if (missing) {
    return <div className="flex aspect-square w-full items-center justify-center rounded-md border border-dashed border-cyan-200/50 bg-slate-950/80 text-4xl font-bold text-cyan-100 shadow-inner shadow-cyan-950/30">?</div>;
  }

  return (
    <div className="grid aspect-square w-full grid-cols-3 overflow-hidden rounded-md border border-cyan-100/45 bg-cyan-100/35 p-px shadow-[0_0_14px_rgb(34_211_238_/_0.16)]">
      {grid.flat().map((color, index) => (
        <span
          key={`${color}-${index}`}
          className="block min-h-0 min-w-0 border border-slate-200/45"
          style={{ backgroundColor: neonGridColor(color) }}
        />
      ))}
    </div>
  );
}

function ColorGridMatrixCell({ cell }: { cell: unknown }) {
  if (cell === null) return <NeonColorGridTile grid={[]} missing />;
  const grid = readColorGrid(cell);
  return <NeonColorGridTile grid={grid} />;
}

export function ColorGridTransformMatrixRenderer({ visualSpec }: { visualSpec: Record<string, unknown> }) {
  const cells = getArray(visualSpec.cells);
  return (
    <div className="w-full max-w-4xl rounded-lg border border-cyan-200/15 bg-slate-950/60 p-3 shadow-[0_0_24px_rgb(34_211_238_/_0.08)] sm:p-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
        {cells.map((cell, index) => (
          <div key={index} className="flex aspect-square items-center justify-center rounded-lg border border-cyan-100/20 bg-slate-900/80 p-2 shadow-inner shadow-cyan-950/20">
            <ColorGridMatrixCell cell={cell} />
          </div>
        ))}
      </div>
    </div>
  );
}

function NeonSymbolTile({ symbol, missing = false }: { symbol: string; missing?: boolean }) {
  return (
    <div className="flex aspect-square items-center justify-center rounded-lg border border-cyan-100/20 bg-slate-950/80 shadow-inner shadow-cyan-950/30">
      {missing ? (
        <span className="text-3xl font-bold text-cyan-100 sm:text-4xl">?</span>
      ) : (
        <ShapeRenderer spec={{ shape: symbol, color: "cyan", fill: "outline", size: "md" }} />
      )}
    </div>
  );
}

export function NeonSymbolMatrix5x5Renderer({ visualSpec }: { visualSpec: Record<string, unknown> }) {
  const cells = getArray(visualSpec.cells).map((cell) => (cell === null ? null : String(cell)));
  return (
    <div className="w-full max-w-xl rounded-lg border border-cyan-200/15 bg-slate-950/60 p-3 shadow-[0_0_24px_rgb(34_211_238_/_0.08)] sm:p-4">
      <div className="grid grid-cols-5 gap-2">
        {cells.map((symbol, index) => (
          <NeonSymbolTile key={index} symbol={symbol ?? "question"} missing={symbol === null || symbol === "question"} />
        ))}
      </div>
    </div>
  );
}

type NeonTileColor = "cyan" | "yellow" | "pink" | "green" | "dark";
type NeonTileCellKey = "tl" | "tm" | "tr" | "bl" | "bm" | "br";

const neonTileCells: NeonTileCellKey[] = ["tl", "tm", "tr", "bl", "bm", "br"];

function neonCellClass(color: NeonTileColor) {
  const colors: Record<NeonTileColor, string> = {
    cyan: "bg-cyan-300 shadow-[0_0_14px_rgb(103_232_249_/_0.7)]",
    yellow: "bg-yellow-300 shadow-[0_0_14px_rgb(253_224_71_/_0.65)]",
    pink: "bg-pink-400 shadow-[0_0_14px_rgb(244_114_182_/_0.65)]",
    green: "bg-emerald-400 shadow-[0_0_14px_rgb(52_211_153_/_0.65)]",
    dark: "bg-slate-950"
  };
  return colors[color] ?? colors.dark;
}

function NeonOddTile({
  tile,
  selected = false,
  correct = false,
  readOnly = false,
  onSelect
}: {
  tile: Record<string, unknown>;
  selected?: boolean;
  correct?: boolean;
  readOnly?: boolean;
  onSelect?: (label: string) => void;
}) {
  const label = String(tile.label ?? "");
  const cells = tile.cells && typeof tile.cells === "object" ? (tile.cells as Partial<Record<NeonTileCellKey, NeonTileColor>>) : {};

  return (
    <button
      type="button"
      disabled={readOnly}
      onClick={() => {
        if (!readOnly) onSelect?.(label);
      }}
      className={clsx(
        "rounded-lg border p-2 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-200",
        correct
          ? "border-emerald-200/85 bg-emerald-200/10 shadow-[0_0_22px_rgb(110_231_183_/_0.16)]"
          : selected
            ? "border-cyan-200/85 bg-cyan-200/10 shadow-[0_0_22px_rgb(34_211_238_/_0.18)]"
            : "border-cyan-100/15 bg-slate-900/80 hover:border-cyan-200/50 hover:bg-slate-900",
        readOnly ? "cursor-default" : ""
      )}
      aria-pressed={selected || correct}
    >
      <span className="mb-1.5 block text-center text-xs font-bold text-cyan-100">{label}</span>
      <span className="grid aspect-[3/2] grid-cols-3 overflow-hidden rounded-md border border-cyan-100/35 bg-slate-950 p-px">
        {neonTileCells.map((cell) => (
          <span key={cell} className={`border border-slate-700/80 ${neonCellClass(cells[cell] ?? "dark")}`} />
        ))}
      </span>
    </button>
  );
}

export function NeonTileOddOneOutRenderer({
  visualSpec,
  selectedOption,
  onSelect,
  readOnly = false,
  revealCorrectOption
}: {
  visualSpec: Record<string, unknown>;
  selectedOption?: string | null;
  onSelect?: (optionId: string) => void;
  readOnly?: boolean;
  revealCorrectOption?: string;
}) {
  const tiles = getArray(visualSpec.tiles).filter((tile): tile is Record<string, unknown> => Boolean(tile && typeof tile === "object"));
  return (
    <div className="w-full max-w-4xl rounded-lg border border-cyan-200/15 bg-slate-950/60 p-3 shadow-[0_0_24px_rgb(34_211_238_/_0.08)] sm:p-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5 sm:gap-3">
        {tiles.map((tile, index) => (
          <NeonOddTile
            key={`${String(tile.label ?? index)}-${index}`}
            tile={tile}
            selected={selectedOption === tile.label}
            correct={revealCorrectOption === tile.label}
            readOnly={readOnly}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

function domeFill(color: string) {
  const colors: Record<string, string> = {
    purple: "rgb(126 87 194)",
    orange: "rgb(249 115 22)",
    lime: "rgb(132 204 22)",
    mutedOrange: "rgb(180 116 64)"
  };
  return colors[color] ?? colors.purple;
}

function RoundedDomeShape({ color, variant = "standard" }: { color: string; variant?: string }) {
  const rotation = variant === "rotated" ? 90 : 0;
  return (
    <svg className="h-full w-full max-w-[116px]" viewBox="0 0 100 100" role="img" aria-label="Στρογγυλεμένο χρωματιστό σχήμα" shapeRendering="geometricPrecision">
      <g transform={`rotate(${rotation} 50 50)`}>
        <path
          d="M50 10C72 10 90 28 90 50V88H26C17 88 10 81 10 72V50C10 28 28 10 50 10Z"
          fill={domeFill(color)}
          stroke="rgb(15 23 42)"
          strokeOpacity="0.12"
          strokeWidth="2"
        />
      </g>
    </svg>
  );
}

function RoundedDomeColorCell({ cell }: { cell: Record<string, unknown> | null }) {
  if (!cell) {
    return <div className="flex aspect-square items-center justify-center rounded-md border border-dashed border-slate-400 bg-slate-100 text-4xl font-bold text-slate-500">?</div>;
  }
  return <RoundedDomeShape color={String(cell.color ?? "purple")} variant={String(cell.variant ?? "standard")} />;
}

export function RoundedDomeColorMatrixRenderer({ visualSpec }: { visualSpec: Record<string, unknown> }) {
  const cells = getArray(visualSpec.cells);
  return (
    <div className="w-full max-w-lg rounded-lg border border-white/10 bg-slate-950/35 p-3 sm:p-4">
      <div className="grid grid-cols-3 gap-2 rounded-md border border-slate-300 bg-slate-100 p-2">
        {cells.map((cell, index) => (
          <div key={index} className="flex aspect-square items-center justify-center rounded-sm border border-slate-300 bg-white p-2">
            <RoundedDomeColorCell cell={cell && typeof cell === "object" ? (cell as Record<string, unknown>) : null} />
          </div>
        ))}
      </div>
    </div>
  );
}

function quadrantFill(color: string) {
  const colors: Record<string, string> = {
    red: "rgb(239 68 68)",
    green: "rgb(34 197 94)",
    blue: "rgb(37 99 235)",
    yellow: "rgb(250 204 21)"
  };
  return colors[color] ?? colors.red;
}

function quadrantShapeElement(shape: string) {
  if (shape === "circle") return <circle cx="50" cy="50" r="38" />;
  if (shape === "diamond") return <polygon points="50 8 92 50 50 92 8 50" />;
  if (shape === "octagon") return <polygon points="32 10 68 10 90 32 90 68 68 90 32 90 10 68 10 32" />;
  return <rect x="12" y="12" width="76" height="76" />;
}

function QuadrantColorShape({ shape, quadrants, id }: { shape: string; quadrants: Record<string, unknown>; id: string }) {
  const clipId = `quadrant-shape-${id}`;
  const cells = [
    { id: "tl", x: 0, y: 0, color: String(quadrants.tl ?? "red") },
    { id: "tr", x: 50, y: 0, color: String(quadrants.tr ?? "green") },
    { id: "bl", x: 0, y: 50, color: String(quadrants.bl ?? "blue") },
    { id: "br", x: 50, y: 50, color: String(quadrants.br ?? "yellow") }
  ];

  return (
    <svg className="h-full w-full max-w-[116px]" viewBox="0 0 100 100" role="img" aria-label="Σχήμα με χρωματιστά τεταρτημόρια" shapeRendering="geometricPrecision">
      <defs>
        <clipPath id={clipId}>{quadrantShapeElement(shape)}</clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {cells.map((cell) => (
          <rect key={cell.id} x={cell.x} y={cell.y} width="50" height="50" fill={quadrantFill(cell.color)} />
        ))}
      </g>
      <g clipPath={`url(#${clipId})`} stroke="rgb(0 0 0)" strokeWidth="2.2">
        <line x1="50" y1="0" x2="50" y2="100" />
        <line x1="0" y1="50" x2="100" y2="50" />
      </g>
      <g fill="none" stroke="rgb(0 0 0)" strokeWidth="2.8">{quadrantShapeElement(shape)}</g>
    </svg>
  );
}

function QuadrantColorShapeCell({ cell, index }: { cell: Record<string, unknown> | null; index: number }) {
  if (!cell) {
    return <div className="flex aspect-square items-center justify-center rounded-md border border-dashed border-slate-400 bg-slate-100 text-4xl font-bold text-slate-500">?</div>;
  }
  const quadrants = cell.quadrants && typeof cell.quadrants === "object" ? (cell.quadrants as Record<string, unknown>) : {};
  return <QuadrantColorShape shape={String(cell.shape ?? "square")} quadrants={quadrants} id={`main-${index}`} />;
}

export function QuadrantColorShapeMatrixRenderer({ visualSpec }: { visualSpec: Record<string, unknown> }) {
  const cells = getArray(visualSpec.cells);
  return (
    <div className="w-full max-w-lg rounded-lg border border-white/10 bg-slate-950/35 p-3 sm:p-4">
      <div className="grid grid-cols-3 gap-2 rounded-md border border-slate-300 bg-slate-100 p-2">
        {cells.map((cell, index) => (
          <div key={index} className="flex aspect-square items-center justify-center rounded-sm border border-slate-300 bg-white p-2">
            <QuadrantColorShapeCell cell={cell && typeof cell === "object" ? (cell as Record<string, unknown>) : null} index={index} />
          </div>
        ))}
      </div>
    </div>
  );
}

function CycleSymbolIcon({ symbol }: { symbol: string }) {
  if (symbol === "R") return <span className="block h-3.5 w-3.5 rounded-full bg-rose-500 sm:h-4 sm:w-4" />;
  if (symbol === "B") {
    return (
      <svg className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 20 20" aria-hidden="true">
        <path d="M10 2 18 17H2Z" fill="rgb(14 165 233)" />
      </svg>
    );
  }
  return <span className="block h-3.5 w-3.5 bg-yellow-300 sm:h-4 sm:w-4" />;
}

function MiniGridSymbolCycleBlock({ grid }: { grid: string[][] }) {
  return (
    <div className="grid aspect-square w-full grid-cols-3 overflow-hidden rounded-md border border-slate-700 bg-slate-700 p-px">
      {grid.flat().map((symbol, index) => (
        <span key={`${symbol}-${index}`} className="flex min-h-0 min-w-0 items-center justify-center border border-slate-300 bg-white">
          <CycleSymbolIcon symbol={symbol} />
        </span>
      ))}
    </div>
  );
}

function MiniGridSymbolCycleCell({ cell }: { cell: Record<string, unknown> | null }) {
  if (!cell) {
    return <div className="flex aspect-square items-center justify-center rounded-md border border-dashed border-amber-200/45 bg-stone-900/70 text-4xl font-bold text-amber-100">?</div>;
  }
  const grid = Array.isArray(cell.grid) ? (cell.grid as unknown[]).map((row) => (Array.isArray(row) ? row.map(String) : [])) : [];
  return <MiniGridSymbolCycleBlock grid={grid.length ? grid : [["Y", "R", "B"], ["B", "Y", "R"], ["R", "B", "Y"]]} />;
}

export function MiniGridSymbolCycleMatrixRenderer({ visualSpec }: { visualSpec: Record<string, unknown> }) {
  const cells = getArray(visualSpec.cells);
  return (
    <div className="w-full max-w-xl rounded-lg border border-white/10 bg-stone-950/70 p-3 sm:p-4">
      <div className="grid grid-cols-3 gap-2 rounded-md border border-amber-100/15 bg-stone-900 p-2">
        {cells.map((cell, index) => (
          <div key={index} className="flex aspect-square items-center justify-center rounded-sm border border-amber-100/15 bg-stone-800/80 p-1.5 sm:p-2">
            <MiniGridSymbolCycleCell cell={cell && typeof cell === "object" ? (cell as Record<string, unknown>) : null} />
          </div>
        ))}
      </div>
    </div>
  );
}

function MiniGridSymbolBlock({ block }: { block: Record<string, unknown> | null }) {
  if (!block) {
    return <div className="flex aspect-square items-center justify-center rounded-lg border border-dashed border-cyan-200/45 bg-slate-950/70 text-3xl font-bold text-cyan-100">?</div>;
  }

  const symbol = String(block.symbol ?? "diamond");
  const filled = new Set(Array.isArray(block.filled) ? block.filled.map(String) : []);
  const symbolCells = new Set(Array.isArray(block.symbolCells) ? block.symbolCells.map(String) : [...filled]);
  const glyph = symbol === "star" ? "★" : symbol === "circle" ? "●" : "◆";
  const cells = ["tl", "tr", "bl", "br"];

  return (
    <div className="grid aspect-square grid-cols-2 overflow-hidden rounded-lg border border-cyan-100/40 bg-slate-100 p-1">
      {cells.map((cell) => {
        const isFilled = filled.has(cell);
        return (
          <span key={cell} className={`flex items-center justify-center border border-slate-500/60 text-xl font-black leading-none sm:text-2xl ${isFilled ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-950"}`}>
            {symbolCells.has(cell) ? glyph : null}
          </span>
        );
      })}
    </div>
  );
}

export function MiniGridSymbolCompletionRenderer({ visualSpec }: { visualSpec: Record<string, unknown> }) {
  const cells = getArray(visualSpec.cells);
  return (
    <div className="w-full max-w-lg rounded-lg border border-white/10 bg-slate-950/35 p-3 sm:p-4">
      <div className="grid grid-cols-3 gap-2">
        {cells.map((cell, index) => (
          <MiniGridSymbolBlock key={index} block={cell && typeof cell === "object" ? (cell as Record<string, unknown>) : null} />
        ))}
      </div>
    </div>
  );
}

function HexCrossShape({ active }: { active: string[] }) {
  const activeSet = new Set(active.map(String));
  const positions = [
    { id: "top", cx: 50, cy: 25 },
    { id: "left", cx: 30, cy: 58 },
    { id: "right", cx: 70, cy: 58 },
    { id: "bottom", cx: 50, cy: 91 }
  ];
  const points = (cx: number, cy: number) => `${cx},${cy - 23} ${cx + 20},${cy - 11.5} ${cx + 20},${cy + 11.5} ${cx},${cy + 23} ${cx - 20},${cy + 11.5} ${cx - 20},${cy - 11.5}`;

  return (
    <svg className="h-full w-full max-w-[116px]" viewBox="0 0 100 116" role="img" aria-label="Σχήμα από τέσσερα εξάγωνα" shapeRendering="geometricPrecision">
      <g stroke="rgb(248 250 252)" strokeWidth="8" strokeLinejoin="miter" strokeMiterlimit="2">
        {positions.map((position) => (
          <polygon key={`back-${position.id}`} points={points(position.cx, position.cy)} fill={activeSet.has(position.id) ? "rgb(2 6 23)" : "rgb(255 255 255)"} />
        ))}
      </g>
      <g stroke="rgb(0 0 0)" strokeWidth="4" strokeLinejoin="miter" strokeMiterlimit="2">
        {positions.map((position) => (
          <polygon key={position.id} points={points(position.cx, position.cy)} fill={activeSet.has(position.id) ? "rgb(0 0 0)" : "rgb(255 255 255)"} />
        ))}
      </g>
    </svg>
  );
}

export function HexXorRotationMatrixRenderer({ visualSpec }: { visualSpec: Record<string, unknown> }) {
  const cells = getArray(visualSpec.cells);

  return (
    <div className="w-full max-w-2xl rounded-lg border border-white/10 bg-slate-950/35 p-3 sm:p-4">
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {cells.map((cell, index) => {
          const active = cell && typeof cell === "object" && Array.isArray((cell as Record<string, unknown>).active) ? ((cell as Record<string, unknown>).active as unknown[]).map(String) : null;
          return (
            <div key={index} className="flex aspect-square min-h-0 items-center justify-center rounded-lg border border-white/10 bg-white/[0.045] p-2 sm:p-3">
              {active ? <HexCrossShape active={active} /> : <span className="text-3xl font-bold text-cyan-100 sm:text-4xl">?</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function AttentionTargetRenderer({ visualSpec, countMode = false }: { visualSpec: Record<string, unknown>; countMode?: boolean }) {
  const grid = getArray(visualSpec.grid).map(String);
  const target = String(visualSpec.target ?? grid[0] ?? "circle");
  return (
    <div className="w-full max-w-md rounded-lg border border-white/10 bg-slate-950/35 p-3">
      <p className="mb-3 text-center text-xs font-semibold text-slate-400">{countMode ? "Μέτρησε τον στόχο" : "Στόχος"}</p>
      <div className="mb-3 flex justify-center">
        <VisualToken value={target} target />
      </div>
      <div className="grid grid-cols-4 gap-2">
        {grid.map((shape, index) => (
          <div key={`${shape}-${index}`} className="flex min-h-14 items-center justify-center rounded-lg border border-white/10 bg-white/[0.045]">
            <VisualToken value={shape} target={shape === target} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function CubeFoldingLiteRenderer({ visualSpec }: { visualSpec: Record<string, unknown> }) {
  const rotation = Number(visualSpec.targetRotation ?? 90);
  return (
    <div className="grid w-full max-w-md grid-cols-[1fr_0.8fr] gap-4 rounded-lg border border-white/10 bg-slate-950/35 p-4">
      <div className="grid grid-cols-3 gap-2">
        {[0, 1, 2, 3, 4, 5].map((cell) => (
          <div key={cell} className="aspect-square rounded-lg border border-cyan-200/25 bg-cyan-200/10" />
        ))}
      </div>
      <div className="flex items-center justify-center">
        <ShapeRenderer spec={{ shape: "hexagon", color: "violet", rotation, size: "lg" }} />
      </div>
    </div>
  );
}

export function MissingPartTileRenderer({ visualSpec }: { visualSpec: Record<string, unknown> }) {
  return (
    <div className="grid w-full max-w-sm grid-cols-2 gap-2 rounded-lg border border-white/10 bg-slate-950/35 p-3">
      <div className="flex aspect-square items-center justify-center rounded-lg border border-white/10 bg-white/[0.045]">
        <ShapeRenderer spec={{ shape: String(visualSpec.baseShape ?? "square"), color: String(visualSpec.color ?? "cyan"), size: "lg" }} />
      </div>
      <div className="flex aspect-square items-center justify-center rounded-lg border border-dashed border-cyan-200/40 bg-cyan-200/5 text-2xl font-bold text-cyan-100">?</div>
    </div>
  );
}

export function FigureCompletionRenderer({ visualSpec }: { visualSpec: Record<string, unknown> }) {
  const missingCorner = String(visualSpec.missingCorner ?? "top-right");
  return (
    <div className="flex w-full justify-center rounded-lg border border-white/10 bg-slate-950/35 p-4">
      <svg className="h-auto w-full max-w-md" viewBox="0 0 420 420" role="img" aria-label="Σχήμα με ελλείπον κομμάτι">
        <rect x="44" y="44" width="332" height="332" rx="10" fill="rgb(2 6 23)" stroke="rgb(148 163 184)" strokeWidth="5" />
        <path d="M44 44 376 376" stroke="rgb(103 232 249)" strokeWidth="7" strokeLinecap="round" />
        <path d="M44 376 376 44" stroke="rgb(103 232 249)" strokeWidth="7" strokeLinecap="round" />
        <path d="M210 44v332M44 210h332" stroke="rgb(103 232 249)" strokeWidth="5" strokeLinecap="round" opacity="0.92" />
        <circle cx="127" cy="127" r="18" fill="none" stroke="rgb(103 232 249)" strokeWidth="6" />
        <circle cx="293" cy="293" r="18" fill="none" stroke="rgb(103 232 249)" strokeWidth="6" />
        <path d="M122 298h58v58" stroke="rgb(103 232 249)" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M264 98h58v58" stroke="rgb(103 232 249)" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" opacity={missingCorner === "top-right" ? 0 : 1} />
        <path d="M210 44h166v166H210Z" fill="rgb(15 23 42)" stroke="rgb(248 250 252)" strokeWidth="5" strokeDasharray="13 10" />
        <text x="293" y="138" fill="rgb(165 243 252)" fontSize="52" fontWeight="700" textAnchor="middle">
          ?
        </text>
      </svg>
    </div>
  );
}
